
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { TaskColumn } from './TaskColumn';
import { TaskCardOverlay } from './TaskCard';
import { TaskWithLead } from '@/types/taskFilters';
import { Task } from '@/types/lead';
import EditTaskDialog from '@/components/business/tasks/EditTaskDialog';

interface TaskBoardProps {
  pendingTasks: TaskWithLead[];
  inProgressTasks: TaskWithLead[];
  overdueTasks: TaskWithLead[];
  completedTasks: TaskWithLead[];
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onDeleteTask: (taskId: string) => void;
  onCompleteTask: (taskId: string) => void;
  getPriorityBadge: (priority: string) => React.ReactNode;
}

export const TaskBoard: React.FC<TaskBoardProps> = ({
  pendingTasks,
  inProgressTasks,
  overdueTasks,
  completedTasks,
  onUpdateTask,
  onDeleteTask,
  onCompleteTask,
  getPriorityBadge,
}) => {
  const [activeTask, setActiveTask] = useState<TaskWithLead | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveTask(active.data.current?.task || null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    // This will be used in the future for reordering within columns
  };

  const findColumnForId = (id: string): string | null => {
    if (['pending-column', 'in-progress-column', 'overdue-column', 'completed-column'].includes(id)) {
      return id;
    }
    if (pendingTasks.some(t => t.id === id)) return 'pending-column';
    if (inProgressTasks.some(t => t.id === id)) return 'in-progress-column';
    if (overdueTasks.some(t => t.id === id)) return 'overdue-column';
    if (completedTasks.some(t => t.id === id)) return 'completed-column';
    return null;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) { setActiveTask(null); return; }

    const taskId = active.id as string;
    const destination = findColumnForId(over.id as string);

    if (!destination || !activeTask) { setActiveTask(null); return; }

    const sourceColumn = findColumnForId(taskId);
    if (sourceColumn === destination) { setActiveTask(null); return; }

    let newStatus: 'pending' | 'in_progress' | 'completed' | null = null;
    if (destination === 'pending-column') newStatus = 'pending';
    else if (destination === 'in-progress-column') newStatus = 'in_progress';
    else if (destination === 'completed-column') newStatus = 'completed';

    if (newStatus) {
      if (newStatus === 'completed') onCompleteTask(taskId);
      else onUpdateTask(taskId, { status: newStatus });
    }

    setActiveTask(null);
  };

  const handleEditTask = (task: TaskWithLead) => {
    setEditingTask(task);
  };

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
  ];

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="relative">
          {/* Scroll indicator shadows for mobile */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10 md:hidden" />
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10 md:hidden" />
          
          <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-6 min-h-[400px] md:min-h-[500px] px-1 sm:px-2 scrollbar-hide snap-x snap-mandatory md:snap-none">
          <TaskColumn
            id="pending-column"
            title="Pending"
            tasks={pendingTasks}
            count={pendingTasks.length}
            onEdit={handleEditTask}
            onDelete={onDeleteTask}
            onComplete={onCompleteTask}
            getPriorityBadge={getPriorityBadge}
          />

          <TaskColumn
            id="in-progress-column"
            title="In Progress"
            tasks={inProgressTasks}
            count={inProgressTasks.length}
            onEdit={handleEditTask}
            onDelete={onDeleteTask}
            onComplete={onCompleteTask}
            getPriorityBadge={getPriorityBadge}
          />
          <TaskColumn
            id="overdue-column"
            title="Overdue"
            tasks={overdueTasks}
            count={overdueTasks.length}
            onEdit={handleEditTask}
            onDelete={onDeleteTask}
            onComplete={onCompleteTask}
            getPriorityBadge={getPriorityBadge}
          />
          
          <TaskColumn
            id="completed-column"
            title="Completed"
            tasks={completedTasks}
            count={completedTasks.length}
            onEdit={handleEditTask}
            onDelete={onDeleteTask}
            onComplete={onCompleteTask}
            getPriorityBadge={getPriorityBadge}
          />
          </div>
        </div>
        {createPortal(
          <DragOverlay dropAnimation={null}>
            {activeTask ? (
              <TaskCardOverlay
                task={activeTask}
                onEdit={() => {}}
                onDelete={() => {}}
                onComplete={() => {}}
                getPriorityBadge={getPriorityBadge}
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
      
      {editingTask && (
        <EditTaskDialog
          isOpen={!!editingTask}
          setIsOpen={() => setEditingTask(null)}
          task={editingTask}
          onSave={onUpdateTask}
          priorityOptions={priorityOptions}
        />
      )}
    </>
  );
};
